import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  BehaviorSubject, catchError, filter, forkJoin, interval, map, Observable, of, shareReplay, Subject, Subscription,
  switchMap, takeUntil,
} from 'rxjs';
import { tap } from 'rxjs/operators';
import { RETRY_ALLOWED } from 'src/app/core/interceptors/http-error.interceptor';
import { McpRelayService } from 'src/app/core/rest-services/mcp-relay.service';
import { environment } from 'src/environments/environment';

export type LMS_NotificationType = 'info' | 'success' | 'warning' | 'error' | 'none';


interface LMSHighlightEvent {
  type: LMS_NotificationType;
  target: string; // e.g. an element ID, component key, or class name
  message: string;
}

interface McpQuotePollingResponse {
  items?: any[];
  approvals?: any[];
  data?: any[];
  nextToken?: string;
  nextPollInMs?: number;
}


@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly pollingInterval = 60000; // 30 seconds
  private readonly minPollingInterval = 30000;
  private readonly maxPollingInterval = 300000;
  private readonly mcpFailureCooldownMs = 300000;
  private readonly notificationPollingDebug = !environment.production;
  private subscriptions: Subscription[];
  statusAnimationState: 'normal' | 'pulsed' = 'normal';

  private awaitingURL = `${environment.apiUrl}/loan/api/quote/awaitingQuotes`;
  private quote_apiURL = `${environment.apiUrl}/loan/api/quote/quickSearch/awaiting`;
  private managerQuoteApiUrl = `${environment.apiUrl}/loan/api/client/ltApprovalQueue`;
  private _isQuoteFound = false;

  private ads = [{
      title: '',
      body: '',
      options: { closeButton: true, tapToDismiss: false, progressBar: true}
    }];

  constructor(private http: HttpClient, private mcpRelayService: McpRelayService) {
    this.subscriptions = [];
  }

  private notificationSubject = new Subject<LMSHighlightEvent>();
  stream$ = this.notificationSubject.asObservable();

  private badgeCount$ = new BehaviorSubject<number>(0);
  readonly badge$ = this.badgeCount$.asObservable();


  setBadgeCount(count: number) {
    this.badgeCount$.next(count);
  }

  clearBadge() {
    this.badgeCount$.next(0);
  }


  notify(event: LMSHighlightEvent) {
    this.notificationSubject.next(event);
  }

  notifytarget(event: LMSHighlightEvent) {
    document.querySelectorAll(`.panel-item.${event.target}`)?.forEach(el => {
      el.classList.add('animate-highlight');
      setTimeout(() => el.classList.remove('animate-highlight'), 1000);
    });
  }

  startPolling(endpoint: string, callback: (events: any[]) => void): void {
    const sub = interval(this.pollingInterval).pipe(takeUntil(this.destroy$),switchMap(() => this.http.get<any[]>(endpoint)));
    const subscribe = sub.subscribe({
        next: events => callback(events),
        error: err => console.error('Polling error:', err)
      });

    this.subscriptions.push(subscribe);
  }
  private destroy$ = new Subject<void>();
  private items$ = new BehaviorSubject<any[]>([]);
  readonly streamitems$ = this.items$.asObservable();
  private quotePolling$?: Observable<any[]>;
  private readonly quotePollingSubscriptions = new Map<(quotes: any[]) => void, Subscription>();
  private nextPollHintMs: number | null = null;
  private lastQuotePollAt = 0;
  private mcpDisabledUntil = 0;
  private quoteCursor: string | null = null;
  private lastPollingTransport: 'mcp' | 'legacy' | null = null;

  private get currentUserProfile(): any | null {
    const localUser = localStorage.getItem('LocalUser');
    return localUser ? JSON.parse(localUser) : null;
  }

  private get currentUserJobTitle(): string {
    return (this.currentUserProfile?.jobTitle?.jobTitle || '').toLowerCase();
  }

  private getQuotePollingRequest(): Observable<any[]> {
    if (this.shouldUseMcpRelay()) {
      this.logPollingTransport('mcp');
      return this.getMcpQuotePollingRequest().pipe(
        catchError(() => {
          this.logPollingTransport('legacy');
          return this.getLegacyQuotePollingRequest();
        })
      );
    }

    this.logPollingTransport('legacy');
    return this.getLegacyQuotePollingRequest();
  }

  private getLegacyQuotePollingRequest(): Observable<any[]> {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return of([]);
    }

    if (this.currentUserJobTitle === 'area manager' || this.currentUserJobTitle === 'regional manager') {
      let params = new HttpParams()
        .set('status', 'pending')
        .set('type', this.currentUserJobTitle === 'regional manager' ? 'RM' : 'AM');

      if (this.currentUserJobTitle === 'area manager') {
        const areas = this.currentUserProfile?.managedAreas
          ?.map((area: any) => area?.id)
          .filter((areaId: unknown) => areaId !== null && areaId !== undefined)
          .join(',');

        if (areas) {
          params = params.set('areas', areas);
        }
      }

      const resultA = this.http.get<any>(this.quote_apiURL).pipe(
        map((response: any) => Array.isArray(response) ? response : response?.approvals ?? []),
        catchError(() => of([]))
      );
      const resultB = this.http.get<any>(this.managerQuoteApiUrl, { params }).pipe(
        map((response: any) => Array.isArray(response) ? response : response?.approvals ?? []),
        catchError(() => of([]))
      );

      return forkJoin([resultA, resultB]).pipe(
        map(([awaitingQuotes, approvalQueue]) => [...awaitingQuotes, ...approvalQueue])
      );
    }

    return this.http.get<any>(this.quote_apiURL).pipe(
      map((response: any) => Array.isArray(response) ? response : response?.approvals ?? []),
      catchError(() => of([]))
    );
  }

  private shouldUseMcpRelay(): boolean {
    if ((environment as any).mcpNotificationsEnabled === false) {
      return false;
    }

    if (!environment.mcpApiUrl) {
      return false;
    }

    return Date.now() >= this.mcpDisabledUntil;
  }

  private logPollingTransport(transport: 'mcp' | 'legacy'): void {
    if (!this.notificationPollingDebug || this.lastPollingTransport === transport) {
      return;
    }

    this.lastPollingTransport = transport;
    console.info(`[NotificationService] quote polling transport: ${transport}`);
  }

  private getMcpQuotePollingRequest(): Observable<any[]> {
    const payload = {
      operation: 'notifications.awaitingQuotes',
      role: this.currentUserJobTitle,
      managedAreas: this.currentUserProfile?.managedAreas
        ?.map((area: any) => area?.id)
        .filter((areaId: unknown) => areaId !== null && areaId !== undefined),
      sinceToken: this.quoteCursor,
    };

    return this.mcpRelayService
      .post<McpQuotePollingResponse>(payload, {
        context: new HttpContext().set(RETRY_ALLOWED, true)
      })
      .pipe(
        map((response) => {
          if(!response) {
            return [];
          }
          const items = this.extractMcpItems(response);
          this.quoteCursor = response?.nextToken ?? this.quoteCursor;
          this.nextPollHintMs = this.normalizePollHint(response?.nextPollInMs);
          this.mcpDisabledUntil = 0;
          return items;
        }),
        catchError((error) => {
          this.nextPollHintMs = null;
          this.mcpDisabledUntil = Date.now() + this.mcpFailureCooldownMs;
          throw error;
        }),
      );
  }

  private extractMcpItems(response: McpQuotePollingResponse | null | undefined): any[] {
    if (!response) {
      return [];
    }

    if (Array.isArray(response.items)) {
      return response.items;
    }

    if (Array.isArray(response.approvals)) {
      return response.approvals;
    }

    if (Array.isArray(response.data)) {
      return response.data;
    }

    return [];
  }

  private normalizePollHint(value: number | null | undefined): number | null {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return null;
    }

    return Math.min(this.maxPollingInterval, Math.max(this.minPollingInterval, value));
  }

  private canRunQuotePollNow(): boolean {
    const now = Date.now();
    const requiredGap = this.nextPollHintMs ?? this.pollingInterval;

    if (this.lastQuotePollAt === 0) {
      this.lastQuotePollAt = now;
      return true;
    }

    if ((now - this.lastQuotePollAt) < requiredGap) {
      return false;
    }

    this.lastQuotePollAt = now;
    return true;
  }

  private getQuotePollingStream(): Observable<any[]> {
    if (!this.quotePolling$) {
      this.quotePolling$ = interval(this.pollingInterval).pipe(
        takeUntil(this.destroy$),
        filter(() => this.canRunQuotePollNow()),
        switchMap(() => this.getQuotePollingRequest()),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }

    return this.quotePolling$;
  }

  public startQuotePolling(onStatusChange: (quotes: any[]) => void): Subscription {
    const existingSubscription = this.quotePollingSubscriptions.get(onStatusChange);
    if (existingSubscription && !existingSubscription.closed) {
      return existingSubscription;
    }

    const subscription = this.getQuotePollingStream().pipe(
      tap((items: any[]) => onStatusChange(items))
    ).subscribe({
      error: (err) => console.error('Quote polling failed:', err)
    });

    subscription.add(() => this.quotePollingSubscriptions.delete(onStatusChange));
    this.quotePollingSubscriptions.set(onStatusChange, subscription);

    return subscription;
  }
  private adsSubject = new BehaviorSubject<any[]>(this.ads);

  getAds(): Observable<any[]> {
    return this.adsSubject.asObservable();
  }

  setAds(ads: any[]) {
    this.ads = ads;
    this.adsSubject.next(ads);
  }

  stopPolling(): void {
    this.quotePollingSubscriptions.forEach((subscription) => subscription.unsubscribe());
    this.quotePollingSubscriptions.clear();

    // Signal all takeUntil operators to complete
    this.destroy$.next();
    this.destroy$.complete();

    // Unsubscribe from all tracked subscriptions
    if (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.forEach(sub => sub.unsubscribe());
      this.subscriptions = [];
    }

    // Reset state
    this.quotePolling$ = undefined;
    this.quoteCursor = null;
    this.nextPollHintMs = null;
    this.lastQuotePollAt = 0;
    this.mcpDisabledUntil = 0;
    this.lastPollingTransport = null;

    // Recreate destroy$ for potential restart
    this.destroy$ = new Subject<void>();
  }
}
